import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core';

import { SlipState } from '@bpos/v1/my-bets';
import { AccaBoostConfig, CrmConfig } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { isEdsPromoTokenBetAndGet } from 'packages/sports/common/betslip/modules/reward-tokens/services/reward-tokens.utils';

import { EarlyPayoutUtils } from '../../../my-bets-base/early-payout/early-payout-utils';
import { EarlyPayoutOptionErrors } from '../../../my-bets-base/models/early-payout-types';
import { PayoutType, TokenType } from '../../../my-bets-base/models/my-bets-viewmodels';
import { RewardTokenType } from '../../../tokens-base/token-base.models';
import { MyBetsBaseUsedRewardTokenComponent } from './my-bets-base-used-reward-token.component';

export interface IconDetails {
    icon?: string;
    extraClass: string;
}
@Component({
    selector: 'ms-my-bets-used-reward-token',
    templateUrl: 'my-bets-used-reward-token.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MyBetsUsedRewardTokenComponent extends MyBetsBaseUsedRewardTokenComponent {
    @HostBinding('class') className = 'my-bets-used-reward-token';

    constructor(
        private crmConfig: CrmConfig,
        private accaBoostConfig: AccaBoostConfig,
        private earlyPayoutUtils: EarlyPayoutUtils,
    ) {
        super();
    }

    get rewardUsedResettledInfo(): string {
        return this.sitecore.crmTokens.myBets.RewardUsedResettledInfo;
    }

    get isResettled(): boolean {
        return this.isRiskFreeResettled || this.isOddsBoostResettled;
    }

    get showDetails(): boolean {
        return (
            !isEdsPromoTokenBetAndGet(this.betslip.edsPromoTokens) &&
            this.betslip.usedRewardToken?.tokenType === TokenType.RiskFreeToken &&
            !this.betslip.isCachedOut
        );
    }

    get showCashoutDetails(): boolean {
        return this.betslip.usedRewardToken?.tokenType === TokenType.RiskFreeToken && this.betslip.state === SlipState.Open;
    }

    get iconClasses(): IconDetails {
        if (isEdsPromoTokenBetAndGet(this.betslip.edsPromoTokens) && this.crmConfig.tokens.icons.betAndGet) {
            return { icon: this.crmConfig.tokens.icons.betAndGet, extraClass: 'my-bets-used-reward-token__icon--betandget' };
        }
        if (this.crmConfig.tokens.icons.freeBet && this.crmConfig.tokens.icons.oddsBoost) {
            switch (this.betslip.usedRewardToken!.tokenType) {
                case TokenType.FreeBetToken:
                    return { icon: this.crmConfig.tokens.icons.freeBet, extraClass: 'my-bets-used-reward-token__icon--free-bet' };
                case TokenType.OddsBoostToken:
                    return { icon: this.crmConfig.tokens.icons.oddsBoost, extraClass: 'my-bets-used-reward-token__icon--odds-boost' };
                case TokenType.RiskFreeToken:
                    return { icon: this.crmConfig.tokens.icons.riskFree, extraClass: 'my-bets-used-reward-token__icon--risk-free' };
                case TokenType.AccaBoostToken:
                    return { icon: this.accaBoostConfig.icon, extraClass: 'my-bets-used-reward-token__icon--acca-boost' };
                default:
                    return { icon: '', extraClass: '' };
            }
        }

        return { icon: '', extraClass: '' };
    }

    get payoutIconClasses(): IconDetails {
        const iconClass = 'my-bets-used-reward-token__payout-icon';

        if (this.betslip.state === SlipState.Won) {
            return { icon: '', extraClass: '' };
        }

        switch (this.betslip.usedRewardToken?.payoutType) {
            case PayoutType.Cash:
                return { icon: this.crmConfig.tokens.icons.cash, extraClass: `${iconClass}--cash` };
            case PayoutType.FreeBet:
                return { icon: this.crmConfig.tokens.icons.freeBet, extraClass: `${iconClass}--freebet` };
            case PayoutType.Bonus:
                return { icon: this.crmConfig.tokens.icons.bonus, extraClass: `${iconClass}--bonus` };
            default:
                return { icon: '', extraClass: '' };
        }
    }

    get description(): string {
        if (isEdsPromoTokenBetAndGet(this.betslip.edsPromoTokens)) {
            return this.descriptionBetAndGet;
        }
        switch (this.betslip.usedRewardToken!.tokenType) {
            case TokenType.FreeBetToken:
                return this.descriptionFreebet;
            case TokenType.OddsBoostToken:
                return this.descriptionOddsBoost;
            case TokenType.RiskFreeToken:
                return this.descriptionRiskFree;
            case TokenType.AccaBoostToken:
                return this.descriptionAccaBoost;
        }

        return '';
    }

    private get isOddsBoostResettled(): boolean {
        const token = this.betslip.usedRewardToken;
        if (!!token && token.tokenType === TokenType.OddsBoostToken) {
            const isCachedOut = this.betslip.isCachedOut;
            const isWon = this.betslip.state === SlipState.Won;
            const tokenNotRewarded = !!token.additionalInformation!.tokenNotRewarded;
            const negativeResettlementEffect = !!token.additionalInformation!.negativeResettlementEffectOnCustomer;

            return isWon && !isCachedOut && (tokenNotRewarded || negativeResettlementEffect);
        }

        return false;
    }

    private get isRiskFreeResettled(): boolean {
        const token = this.betslip.usedRewardToken;
        if (!!token && token.tokenType === TokenType.RiskFreeToken) {
            const isLost = this.betslip.state === SlipState.Lost;
            const tokenNotRewarded = !!token.additionalInformation!.tokenNotRewarded;

            return isLost && tokenNotRewarded;
        }

        return false;
    }

    private get descriptionOddsBoostResettled(): string {
        return this.sitecore.crmTokens.myBets.OddsBoostUsedResettled;
    }

    private get payoutType(): string {
        switch (this.betslip.usedRewardToken!.payoutType) {
            case PayoutType.Bonus:
                return this.sitecore.crmTokens.myBets.PayoutTypeBonus;
            case PayoutType.FreeBet:
                return this.sitecore.crmTokens.myBets.PayoutTypeFreeBet;
            case PayoutType.Cash:
                return this.sitecore.crmTokens.myBets.PayoutTypeCash;
        }

        return '';
    }

    private get descriptionOddsBoostWon(): string {
        const extraWinnings = this.betslip.usedRewardToken!.additionalInformation!.winningsBoost!;

        return interpolateDoubleBracesString(this.sitecore.crmTokens.myBets.OddsBoostUsedWonTemplate, {
            extraWinnings: this.moneyService.transform(extraWinnings, true),
            payoutType: this.payoutType,
        });
    }

    private get descriptionRiskFreeResettled(): string {
        return this.sitecore.crmTokens.myBets.RiskFreeUsedResettled;
    }

    getRiskFreeRewardTemplate(isIncremental: boolean): string {
        if (this.betslip.state === SlipState.Lost) {
            return isIncremental
                ? this.sitecore.crmTokens.myBets.RiskFreeIncrementalAwardedTemplate
                : this.sitecore.crmTokens.myBets.RiskFreeUsedAwardedTemplate;
        } else {
            return isIncremental
                ? this.sitecore.crmTokens.riskFree.betslipSummary.RiskFreeIncrementalPayoutSummary
                : this.sitecore.crmTokens.riskFree.betslipSummary.RiskFreePayoutSummary;
        }
    }

    get detailsText(): string {
        return this.getRiskFreePayoutDetails();
    }

    get cashingOutDescriptionRiskFree(): string {
        const payoutType = this.betslip.usedRewardToken?.payoutType;
        if (!payoutType) {
            return '';
        }

        const displayPayoutType = this.tokensSummaryService.getDisplayPayoutType(payoutType, RewardTokenType.RiskFreeBet);

        return interpolateDoubleBracesString(this.sitecore.crmTokens.myBets.CashingOutDescription, {
            payoutType: displayPayoutType,
        });
    }

    private get descriptionFreebet(): string {
        const showClarification = this.showFreeBetEarlyPayoutClarification();

        return (
            this.sitecore.crmTokens.myBets.FreeBetUsed +
            (showClarification ? ` ${this.sitecore.crmTokens.myBets.FreeBetEarlyPayoutClarification}` : '')
        );
    }

    private get descriptionOddsBoost(): string {
        if (!this.betslip.isCachedOut) {
            if (this.isOddsBoostResettled) {
                return this.descriptionOddsBoostResettled;
            }

            if (this.betslip.state === SlipState.Won) {
                return this.descriptionOddsBoostWon;
            }
        }

        const acquisitionRewardDescription = this.getRewardTargetDescription(this.betslip.placeBetSignPosting?.rewardTargetType);

        return acquisitionRewardDescription ?? this.sitecore.crmTokens.myBets.OddsBoostUsed;
    }

    private get descriptionRiskFree(): string {
        if (this.betslip.state === SlipState.Lost) {
            if (this.isRiskFreeResettled) {
                return this.descriptionRiskFreeResettled;
            }
        }

        const acquisitionRewardDescription = this.getRewardTargetDescription(this.betslip.placeBetSignPosting?.rewardTargetType);

        return acquisitionRewardDescription ?? this.sitecore.crmTokens.myBets.RiskFreeUsed;
    }

    private get descriptionBetAndGet(): string {
        return this.getRewardTargetDescription(this.betslip.placeBetSignPosting?.rewardTargetType) ?? this.sitecore.crmTokens.myBets.BetAndGetUsed;
    }

    private get descriptionAccaBoost(): string {
        return this.sitecore.crmTokens.myBets.AccaBoostUsed;
    }

    private showFreeBetEarlyPayoutClarification(): boolean {
        if (
            this.betslip.state === SlipState.Open &&
            this.betslip.earlyPayout?.error?.type !== EarlyPayoutOptionErrors.OptionMarketTemplateNotAllowed
        ) {
            return this.earlyPayoutUtils.isEarlyPayoutAvailable(this.betslip);
        }

        return false;
    }
}
